// Non-React `t`: this is a plain util called from render, and i18next
// initializes on import.
import { t, i18next, DEFAULT_LOCALE } from 'i18n'
import { BEAKER_DEFAULT_LABELS } from './beakerDefaultLabels'

/**
 * Label for a step in the break navigation — used by both the TOP nav
 * (Mobile/DesktopNavBar) and the LEFT SideNav.
 *
 * The step type stays the identifier; only the displayed label is localized,
 * via stepShell:navLabel.<STEP_TYPE>.
 *
 * A label an author actually typed wins, since that is authored content rather
 * than chrome. The catch is that most steps arrive carrying a label nobody
 * typed: the Beaker API fills the field with an English default when it is left
 * empty, so the JSON says "Store Finder" while the field in Beaker is blank.
 * Taking those at face value left the navigation in English.
 *
 * So a label counts as authored only when it is not one of Beaker's defaults.
 * Those live in ./beakerDefaultLabels, mirrored from the Java map, and are not
 * the same strings as our own English labels: we show "Reward" where Beaker
 * defaults to "Complete".
 */

/** Compare loosely: the API's casing and spacing are not guaranteed to match ours. */
function normalise(value) {
  return `${value ?? ''}`
    .replace(/<[^>]*>/g, '') // labels are rendered as HTML and may carry markup
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function isBeakerDefault(label, stepType) {
  const beakerDefault = BEAKER_DEFAULT_LABELS[stepType]
  if (beakerDefault && normalise(label) === normalise(beakerDefault)) return true

  // Also treat our own English label as a default. Some step types are not in
  // the Java map, and a break authored before a label was translated can carry
  // exactly the string we display.
  const key = `stepShell:navLabel.${stepType}`
  if (!i18next.exists(key)) return false
  return normalise(label) === normalise(i18next.t(key, { lng: 'en-US' }))
}

export function getStepLabel({ label, stepType }) {
  const key = `stepShell:navLabel.${stepType}`
  const hasTranslation = i18next.exists(key)

  // In English there is nothing to localize, so a label the API sent is shown as it came.
  //
  // Substituting our catalog value for Beaker's default is what puts the navigation into
  // the reader's language, and it is pointless when that language is the one Beaker
  // already used: all it does is swap one English string for another. It rewrote six of
  // them -- Complete became Reward, Photo became Upload A Photo -- which reads as a copy
  // change nobody asked for, and QA caught it as a regression.
  //
  // Those are decisions about English copy, and they should ship as that rather than as a
  // side effect of translating. So the substitution now only applies once a visitor is
  // actually being served another language.
  const localised = i18next.language && i18next.language !== DEFAULT_LOCALE

  if (label && label.length > 0 && (!localised || !isBeakerDefault(label, stepType))) {
    return label
  }

  // exists() rather than a defaultValue: the instance runs with
  // returnEmptyString disabled, so an empty default would come back as the key
  // path.
  if (hasTranslation) {
    return t(key)
  }

  // A default we can recognise but cannot translate is still better shown than
  // turned into a raw enum.
  if (label && label.length > 0) {
    return label
  }

  // unknown step type: show the raw enum as readably as we can
  return `${stepType}`.replace(/_/g, ' ')
}
